import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Send, CheckCircle2, XCircle, RefreshCw, DollarSign, Calendar, Loader2, ChevronRight, History } from "lucide-react";

const STATUS_TONE = {
  SENT:"bg-sky-50 text-sky-700 border-sky-300",
  ACCEPTED:"bg-emerald-50 text-emerald-800 border-emerald-300",
  DECLINED:"bg-rose-50 text-rose-800 border-rose-300",
  COUNTERED:"bg-amber-50 text-amber-800 border-amber-300",
  HIRE_CONFIRMED:"bg-emerald-100 text-emerald-900 border-emerald-400",
  START_DATE_SET:"bg-emerald-100 text-emerald-900 border-emerald-400",
  WITHDRAWN:"bg-slate-100 text-slate-500 border-slate-300",
  EXPIRED:"bg-slate-100 text-slate-500 border-slate-300",
};

export default function CandidateOffers() {
  const nav = useNavigate();
  const [offers, setOffers] = useState([]);
  const [countering, setCountering] = useState(null);
  const [counter, setCounter] = useState({counter_compensation_amount:"", counter_compensation_period:"", counter_start_date:"", counter_notes:"", reason:""});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const {data} = await api.get("/offers"); setOffers(data); }
    catch (e) { toast.error("Failed to load offers"); }
  };
  useEffect(() => { load(); }, []);

  const respond = async (o, action, payload={}) => {
    setBusy(true);
    try {
      await api.post(`/offers/${o.id}/respond`, {action, ...payload});
      toast.success(action === "ACCEPT" ? "Offer accepted — the employer will confirm the hire" :
                     action === "DECLINE" ? "Offer declined" : "Counter sent");
      setCountering(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Response failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-slate-100 px-6 py-3 flex items-center gap-3">
        <button onClick={()=>nav("/portal")} data-testid="co-back" className="text-xs inline-flex items-center gap-1"><ArrowLeft size={12}/> Portal</button>
        <div className="font-display font-bold text-sm">Your offers</div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Wave 4 · Offer response</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Offers waiting for you</h1>
          <p className="text-sm text-slate-500 mt-1">Accept, decline, or counter. Your response is recorded on the offer and prior terms are preserved.</p>
        </div>

        <div className="space-y-3">
          {offers.length === 0 && <div className="p-10 text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-xl">No offers yet.</div>}
          {offers.map(o => (
            <div key={o.id} data-testid={`c-offer-${o.id}`} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">{o.role_title} · v{o.version}</div>
                  <div className="font-display text-2xl font-bold text-slate-900">
                    {(o.counter_compensation_amount ?? o.compensation_amount)?.toLocaleString()} {o.compensation_currency} / {o.counter_compensation_period || o.compensation_period}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{o.employment_type} · {o.work_arrangement} · Workforce {o.workforce_model}</div>
                  {o.proposed_start_date && <div className="text-xs text-slate-600 mt-2 inline-flex items-center gap-1"><Calendar size={11}/> Proposed start: {o.counter_start_date || o.proposed_start_date}</div>}
                  {o.benefits_summary && <div className="text-xs text-slate-600 mt-2">Benefits: {o.benefits_summary}</div>}
                  {o.notes_for_candidate && <div className="text-xs text-slate-600 mt-2 italic">"{o.notes_for_candidate}"</div>}
                  {o.history?.length>0 && <div className="text-[10px] text-slate-500 mt-2 inline-flex items-center gap-1"><History size={11}/> {o.history.length} prior offer version{o.history.length>1?"s":""} preserved</div>}
                </div>
                <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${STATUS_TONE[o.status]||"bg-slate-100 text-slate-600 border-slate-200"}`}>{o.status}</span>
              </div>
              {(o.status === "SENT" || o.status === "COUNTERED") && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button data-testid={`c-accept-${o.id}`} onClick={()=>respond(o, "ACCEPT")} disabled={busy} className="h-9 px-4 rounded bg-emerald-600 text-white text-xs font-semibold inline-flex items-center gap-1"><CheckCircle2 size={12}/> Accept</button>
                  <button data-testid={`c-counter-${o.id}`} onClick={()=>{setCountering(o); setCounter(c=>({...c, counter_compensation_amount: o.compensation_amount, counter_compensation_period: o.compensation_period, counter_start_date: o.proposed_start_date||""}));}} disabled={busy} className="h-9 px-4 rounded bg-amber-500 text-white text-xs font-semibold inline-flex items-center gap-1"><RefreshCw size={12}/> Counter</button>
                  <button data-testid={`c-decline-${o.id}`} onClick={()=>respond(o, "DECLINE")} disabled={busy} className="h-9 px-4 rounded border border-slate-300 text-slate-700 text-xs font-semibold inline-flex items-center gap-1"><XCircle size={12}/> Decline</button>
                </div>
              )}
              {o.status === "HIRE_CONFIRMED" && <div className="mt-3 text-xs text-emerald-700 font-semibold inline-flex items-center gap-1"><CheckCircle2 size={12}/> Hire confirmed — start on {o.agreed_start_date}</div>}
            </div>
          ))}
        </div>
      </main>

      {countering && (
        <div className="fixed inset-0 bg-slate-900/80 z-50 overflow-auto p-4 flex items-start justify-center" onClick={()=>setCountering(null)}>
          <div data-testid="counter-modal" className="max-w-lg w-full bg-white rounded-xl p-6 mt-8" onClick={e=>e.stopPropagation()}>
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-2">Counter offer</h2>
            <p className="text-sm text-slate-500 mb-4">Only fill fields you want to change. Your original offer will remain in history.</p>
            <div className="space-y-3">
              <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Requested compensation</label><input data-testid="cc-amount" type="number" value={counter.counter_compensation_amount} onChange={e=>setCounter(c=>({...c, counter_compensation_amount: parseFloat(e.target.value)||0}))} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/></div>
              <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Preferred start date</label><input data-testid="cc-start" type="date" value={counter.counter_start_date} onChange={e=>setCounter(c=>({...c, counter_start_date: e.target.value}))} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/></div>
              <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Reason / notes</label><textarea data-testid="cc-notes" rows={3} value={counter.counter_notes} onChange={e=>setCounter(c=>({...c, counter_notes: e.target.value, reason: e.target.value.slice(0,140)}))} className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none"/></div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={()=>setCountering(null)} className="h-10 px-4 rounded border border-slate-300 text-slate-700 text-sm font-semibold">Cancel</button>
              <button data-testid="cc-submit" onClick={()=>respond(countering, "COUNTER", counter)} disabled={busy} className="h-10 px-4 rounded bg-amber-500 text-white text-sm font-semibold">{busy&&<Loader2 size={13} className="animate-spin inline"/>} Send counter</button>
            </div>
          </div>
        </div>
      )}
      <Toaster position="top-right" richColors/>
    </div>
  );
}
